import { Vector3 } from 'three';
import { ITexture } from '../../@types/ContextProviderRelated';
import ElementModel from '../../models/standard/ElementModel';
import { ObjectModel } from '../../models/standard/ObjectModel';
import { fetchTextureFromApi } from './fetchTextureFromApi';

// Interface of the element instantiation object
export interface ElementInstantiation {
  element: ElementModel;
  elementData: any;
  neededToFetchTexture: boolean;
  fetchingSuccesful: boolean;
  textureId: string;
}

/**
 * Helper: Instantiate an element from JSON.
 * Fetch the full texture before instantiating.
 */
export async function instantiateElementAsync(elemData: any, isFilesApiAvailable?: boolean): Promise<ElementInstantiation> {
  let texture: ITexture | undefined;
  let neededFetchedTexture = false;
  let fetchingSuccessful = false;
  let textureId = '';

  // Determine texture type and handle accordingly
  if (elemData.texture?.id === -1 && elemData.texture?.color) {
    // Legacy element with inline color
    texture = {
      id: -1,
      name: '',
      material: null,
      color: elemData.texture.color,
      rotation: 0,
      offset: 0,
    } as ITexture;
  } else if (elemData.texture) {
    // Fetch texture from API
    textureId = elemData.texture.id;
    neededFetchedTexture = true;

    const textureResult = await fetchTextureFromApi(elemData.texture, isFilesApiAvailable);
    fetchingSuccessful = textureResult.fetchingSuccesful || textureResult.neededToFetchTexture === false;

    if (textureResult.texture !== undefined && textureResult.texture !== null) {
      texture = textureResult.texture as ITexture;
    }
  }

  // Deserialize object model if present
  const objectModel = elemData.object ? ObjectModel.fromJSON(elemData.object) : undefined;

  // Creating result object
  const element = new ElementModel(
    elemData.type,
    elemData.length,
    elemData.width,
    new Vector3(elemData.position.x, elemData.position.y, elemData.position.z),
    elemData.rotation,
    texture as ITexture,
    elemData.description,
    elemData.useAsTextField,
    elemData.fontSize,
    objectModel,
    elemData.attributes,
    elemData.sideC,
    elemData.triangleAngle
  );

  if (elemData.uuid) {
    element.setUUID(elemData.uuid);
  }

  // Create and return the element
  return {
    element: element,
    elementData: elemData,
    neededToFetchTexture: neededFetchedTexture,
    fetchingSuccesful: fetchingSuccessful,
    textureId,
  };
}
